import React, { useEffect, useState, useCallback } from "react";
import axios from "../../api/axiosAuth";
import { useAuth } from "../../context/AuthContext";
import { API as BASE_API } from "../config"; // ✅ Uso de configuración centralizada
import "../../style/informes.css";

const API_BASE = `${BASE_API}`;

const normalizarLista = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.anios)) return data.anios;
  if (Array.isArray(data?.periodos)) return data.periodos;
  if (Array.isArray(data?.sedes)) return data.sedes;
  return [];
};

export default function Informes() {
  const { token, user } = useAuth();

  const [tipoInforme, setTipoInforme] = useState("estudiantes");

  const [grado, setGrado] = useState("");
  const [curso, setCurso] = useState("");
  const [sede, setSede] = useState(user?.sede_id || "");
  const [sedes, setSedes] = useState([]);

  const [anios, setAnios] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [anioLectivo, setAnioLectivo] = useState("");
  const [periodo, setPeriodo] = useState("");

  const [loading, setLoading] = useState(false);
  const [resultados, setResultados] = useState([]);

  /* ===============================
      🔄 CARGA DE DATOS INICIALES
  =============================== */
  const cargarDatosIniciales = useCallback(async () => {
    if (!token) return;
    try {
      // 1. Cargar sedes
      const resSedes = await axios.get(`${API_BASE}/informes/sedes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSedes(normalizarLista(resSedes.data));

      // 2. Cargar años lectivos
      const resAnios = await axios.get(`${API_BASE}/cronograma/anios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const listaAnios = normalizarLista(resAnios.data);
      setAnios(listaAnios);

      // 3. Seleccionar año activo y sus periodos
      const anioActivo = listaAnios.find((a) => a.activo === 1);
      if (anioActivo) {
        setAnioLectivo(anioActivo.id);
        
        const resPeriodos = await axios.get(`${API_BASE}/cronograma/periodos`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { anio_lectivo_id: anioActivo.id },
        });
        const listaPeriodos = normalizarLista(resPeriodos.data);
        setPeriodos(listaPeriodos);

        const perActivo = listaPeriodos.find((p) => p.activo === 1);
        if (perActivo) setPeriodo(perActivo.id);
      }
    } catch (err) {
      console.error("❌ Error inicialización:", err);
    }
  }, [token]);

  useEffect(() => {
    cargarDatosIniciales();
  }, [cargarDatosIniciales]);

  /* ===============================
      ⚙️ MANEJADORES DE EVENTOS
  =============================== */
  const onChangeAnio = async (e) => {
    const id = e.target.value;
    setAnioLectivo(id);
    setPeriodo("");
    setPeriodos([]);
    if (!id) return;

    try {
      const res = await axios.get(`${API_BASE}/cronograma/periodos`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { anio_lectivo_id: id },
      });
      setPeriodos(normalizarLista(res.data));
    } catch (err) {
      console.error("❌ Error cargando periodos:", err);
    }
  };

  const generarInforme = async () => {
    setLoading(true);
    setResultados([]);
    try {
      const url = `${API_BASE}/informes/${tipoInforme}`;
      const params = {
        grado: grado || undefined,
        curso: curso || undefined,
        sede_id: sede || undefined,
        anio_lectivo_id: anioLectivo || undefined,
        periodo_id: periodo || undefined,
        tenant_id: user?.tenant_id, // Aseguramos el contexto del tenant
      };

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      setResultados(normalizarLista(res.data));
    } catch (err) {
      console.error("❌ Error al generar informe:", err);
      alert("No se obtuvieron datos para los filtros seleccionados.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="informes-container p-6">
      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
        📊 Módulo de Informes - Secretaría
      </h2>

      {/* PANEL DE FILTROS */}
      <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200 mb-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-end">
        
        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-gray-500">TIPO DE INFORME</label>
          <select 
            value={tipoInforme} 
            onChange={(e) => { setTipoInforme(e.target.value); setResultados([]); }}
            className="p-2 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-400 outline-none"
          >
            <option value="estudiantes">Listado de Estudiantes</option>
            <option value="asistencia">Informe de Asistencia</option>
            <option value="notas">Notas / Boletines</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-gray-500">GRADO</label>
          <input
            type="text"
            placeholder="Ej: 11"
            value={grado}
            onChange={(e) => setGrado(e.target.value)}
            className="p-2 border rounded-lg outline-none"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-gray-500">SEDE</label>
          <select value={sede} onChange={(e) => setSede(e.target.value)} className="p-2 border rounded-lg outline-none">
            <option value="">Todas las sedes</option>
            {sedes.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-gray-500">AÑO LECTIVO</label>
          <select value={anioLectivo} onChange={onChangeAnio} className="p-2 border rounded-lg outline-none">
            <option value="">Seleccione año</option>
            {anios.map((a) => (
              <option key={a.id} value={a.id}>{a.anio}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-gray-500">PERIODO</label>
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="p-2 border rounded-lg outline-none">
            <option value="">Opcional (Todos)</option>
            {periodos.map((p) => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
        </div>

        <button 
          onClick={generarInforme} 
          disabled={loading}
          className="bg-blue-600 text-white font-bold p-2 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400"
        >
          {loading ? "Procesando..." : "Consultar"}
        </button>
      </div>

      {/* ÁREA DE RESULTADOS */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        {resultados.length === 0 ? (
          <div className="p-10 text-center text-gray-400 italic">
            No se han encontrado registros para mostrar.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  {tipoInforme === "estudiantes" && (
                    <>
                      <th className="p-4 text-xs font-bold uppercase">ID</th>
                      <th className="p-4 text-xs font-bold uppercase">Nombre Completo</th>
                      <th className="p-4 text-xs font-bold uppercase">Documento</th>
                      <th className="p-4 text-xs font-bold uppercase">Grado/Curso</th>
                      <th className="p-4 text-xs font-bold uppercase">Sede</th>
                    </>
                  )}
                  {tipoInforme === "asistencia" && (
                    <>
                      <th className="p-4 text-xs font-bold uppercase">Estudiante</th>
                      <th className="p-4 text-xs font-bold uppercase">Fecha</th>
                      <th className="p-4 text-xs font-bold uppercase">Estado</th>
                    </>
                  )}
                  {tipoInforme === "notas" && (
                    <>
                      <th className="p-4 text-xs font-bold uppercase">Estudiante</th>
                      <th className="p-4 text-xs font-bold uppercase">Materia</th>
                      <th className="p-4 text-xs font-bold uppercase">Nota</th>
                      <th className="p-4 text-xs font-bold uppercase text-center">Periodo</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {resultados.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    {tipoInforme === "estudiantes" && (
                      <>
                        <td className="p-4 text-sm">{r.id}</td>
                        <td className="p-4 text-sm font-medium">{r.nombres_estudiante} {r.apellidos_estudiante}</td>
                        <td className="p-4 text-sm text-gray-600">{r.documento_estudiante}</td>
                        <td className="p-4 text-sm">{r.grado}°{r.curso}</td>
                        <td className="p-4 text-sm text-gray-500">{r.sede}</td>
                      </>
                    )}
                    {tipoInforme === "asistencia" && (
                      <>
                        <td className="p-4 text-sm font-medium">{r.nombres_estudiante} {r.apellidos_estudiante}</td>
                        <td className="p-4 text-sm text-gray-600">{new Date(r.fecha).toLocaleDateString()}</td>
                        <td className="p-4 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${r.asistencia === 'P' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {r.asistencia === 'P' ? 'Presente' : 'Ausente'}
                          </span>
                        </td>
                      </>
                    )}
                    {tipoInforme === "notas" && (
                      <>
                        <td className="p-4 text-sm font-medium">{r.nombres_estudiante} {r.apellidos_estudiante}</td>
                        <td className="p-4 text-sm text-gray-600">{r.asignatura_nombre || r.asignatura_id}</td>
                        <td className="p-4 text-sm font-bold text-blue-700">{Number(r.nota).toFixed(1)}</td>
                        <td className="p-4 text-sm text-center">{r.periodo_nombre || r.periodo_id}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}